import { useMemo } from 'react';

import { MultiCombobox, type IComboboxOption } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

import { useListAutomationSummaries } from '@/generated/hooks/grimoire/v1/api';

interface IAutomationsMultiSelectProps {
  organizationId: string;
  workspaceId: string;
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export const AutomationsMultiSelect = ({
  organizationId,
  workspaceId,
  selected,
  onChange,
  disabled,
}: IAutomationsMultiSelectProps) => {
  const { t } = useTranslation('settings');

  const { data: automationsData } = useListAutomationSummaries(
    organizationId,
    workspaceId,
    undefined,
    { query: { enabled: !!workspaceId } },
  );

  const comboboxOptions = useMemo<IComboboxOption[]>(
    () =>
      (automationsData?.data?.automation_summaries || [])
        .filter(a => !!a.name)
        .map(a => ({
          value: a.name as string,
          label: a.display_name || (a.name as string),
        })),
    [automationsData],
  );

  const placeholder = t(
    'settings.supportAccess.dialog.placeholders.selectAutomations',
  );

  return (
    <MultiCombobox
      options={comboboxOptions}
      value={selected}
      onValueChange={onChange}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      emptyMessage={t('settings.supportAccess.dialog.empty.noAutomations')}
      disabled={disabled}
      modal
      className="flex w-full"
      buttonClassName="flex w-full"
      popoverClassName="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]"
      getSearchKeywords={option => [option.label]}
      showClear
    />
  );
};
